import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import { Delete as DeleteIcon, Edit as EditIcon, Add as AddIcon } from '@mui/icons-material';
import api from '../api/client';
import { Product, ProductCompetitor, CreateProductCompetitorRequest, UpdateProductCompetitorRequest } from '../types';

interface ProductCompetitorsProps {
  productId: number;
  onUpdate?: () => void;
}

const ProductCompetitors: React.FC<ProductCompetitorsProps> = ({ productId, onUpdate }) => {
  const [competitors, setCompetitors] = useState<ProductCompetitor[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [editingCompetitor, setEditingCompetitor] = useState<ProductCompetitor | null>(null);
  const [formData, setFormData] = useState<CreateProductCompetitorRequest>({
    name: '',
    asin: '',
  });
  const [newCompetitorAsin, setNewCompetitorAsin] = useState('');
  const [bulkAddOpen, setBulkAddOpen] = useState(false);
  const [bulkAsins, setBulkAsins] = useState('');
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [competitorToDelete, setCompetitorToDelete] = useState<ProductCompetitor | null>(null);

  useEffect(() => {
    fetchCompetitors();
  }, [productId]);

  const handleBulkAddCompetitors = async () => {
    const asins = bulkAsins.split(/[\s,]+/).filter(asin => asin.trim() !== '');
    if (asins.length === 0) {
      setError('Please enter at least one ASIN.');
      return;
    }

    try {
      setLoading(true);
      const promises = asins.map(asin => 
        api.createProductCompetitor(productId, { name: asin.trim(), asin: asin.trim() })
      );
      await Promise.all(promises);

      setSuccess('Competitors added successfully.');
      setBulkAddOpen(false);
      setBulkAsins('');
      fetchCompetitors();
      if (onUpdate) onUpdate();
    } catch (err) {
      setError('Failed to add competitors. Please check the ASINs and try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchCompetitors = async () => {
    try {
      setLoading(true);
      const data = await api.getProductCompetitors(productId);
      setCompetitors(data);
      setError('');

    } catch (err) {
      setError('Failed to load competitors');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (competitor?: ProductCompetitor) => {
    if (competitor) {
      setEditingCompetitor(competitor);
      setFormData({
        name: competitor.name,
        asin: competitor.asin,
      });
    } else {
      setEditingCompetitor(null);
      setFormData({
        name: '',
        asin: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    try {
      if (!formData.name || !formData.asin) {
        setError('Name and ASIN are required');
        return;
      }

      if (editingCompetitor) {
        // Update existing competitor
        const updateData: UpdateProductCompetitorRequest = {};
        if (formData.name !== editingCompetitor.name) updateData.name = formData.name;
        if (formData.asin !== editingCompetitor.asin) updateData.asin = formData.asin;

        await api.updateProductCompetitor(productId, editingCompetitor.id, updateData);
        setSuccess('Competitor updated successfully');
      } else {
        // Create new competitor
        await api.createProductCompetitor(productId, formData);
        setSuccess('Competitor added successfully.');
      }
      handleCloseDialog();
      fetchCompetitors();
      if (onUpdate) onUpdate();
    } catch (err) {
      setError('Failed to save competitor');
      console.error(err);
    }
  };

  const handleDeleteClick = (competitor: ProductCompetitor) => {
    setCompetitorToDelete(competitor);
    setConfirmDeleteOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (competitorToDelete) {
      try {
        await api.deleteProductCompetitor(productId, competitorToDelete.id);
        setSuccess('Competitor deleted successfully.');
        fetchCompetitors();
        if (onUpdate) onUpdate();
      } catch (err) {
        setError('Failed to delete competitor');
        console.error(err);
      }
    }
    setConfirmDeleteOpen(false);
    setCompetitorToDelete(null);
  };

  const handleCloseSnackbar = () => {
    setError('');
    setSuccess('');
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          Competitors {competitors.length > 0 && `(${competitors.length})`}
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Competitor
        </Button>
        <Button variant="outlined" onClick={() => setBulkAddOpen(true)} sx={{ ml: 1 }}>
          Bulk Add
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : competitors.length === 0 ? (
        <Typography variant="body1" sx={{ textAlign: 'center', p: 3 }}>
          No competitors added yet. Click "Add Competitor" to get started.
        </Typography>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>ASIN</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {competitors.map((competitor) => (
                <TableRow key={competitor.id}>
                  <TableCell>{competitor.name}</TableCell>
                  <TableCell>{competitor.asin}</TableCell>
                  <TableCell>
                    <IconButton
                      onClick={() => handleOpenDialog(competitor)}
                      size="small"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      onClick={() => handleDeleteClick(competitor)}
                      size="small"
                      sx={{ color: 'error.main' }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={openDialog} onClose={handleCloseDialog} fullWidth maxWidth="sm">
        <DialogTitle>{editingCompetitor ? 'Edit Competitor' : 'Add New Competitor'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            id="name"
            name="name"
            label="Competitor Name"
            type="text"
            fullWidth
            value={formData.name}
            onChange={handleInputChange}
            required
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            id="asin"
            name="asin"
            label="ASIN"
            type="text"
            fullWidth
            value={formData.asin}
            onChange={handleInputChange}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!formData.name || !formData.asin}
          >
            {editingCompetitor ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={bulkAddOpen} onClose={() => setBulkAddOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Bulk Add Competitors</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            id="bulk-asins"
            label="ASINs (separated by comma or new line)"
            type="text"
            fullWidth
            multiline
            rows={4}
            value={bulkAsins}
            onChange={(e) => setBulkAsins(e.target.value)}
            placeholder="e.g. B08C791S3F, B08C72BWR5\nB07PXHRHBH"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkAddOpen(false)}>Cancel</Button>
          <Button onClick={handleBulkAddCompetitors} variant="contained">Add Competitors</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
      >
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the competitor "{competitorToDelete?.name}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProductCompetitors;
